import {
  Alert,
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material'
// oxlint-disable no-unused-vars
import { useEffect, useState } from 'react'

import BackupCodesCard from './BackupCodesCard'
import PersonIcon from '@mui/icons-material/Person'
import UserInformationCard from './UserInformationCard'
import { capitalize } from '../../utilities/stringUtils'
import fetchWithAuth from '../../utilities/fetchWithAuth'
import { useNavigate } from 'react-router-dom'

const InfoItem = ({ label, value }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body1">{value || 'N/A'}</Typography>
  </Box>
)

const UserProfilePage = () => {
  const [user, setUser] = useState({})
  const [dateFormat, setDateFormat] = useState('')
  const [timezone, setTimezone] = useState('')
  const [theme, setTheme] = useState(null)
  const [errors, setErrors] = useState({})
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  })
  const [backupCodes, setBackupCodes] = useState(null)

  const navigate = useNavigate()

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const { data } = await fetchWithAuth('users/me')
        setUser(data)
        setDateFormat(data.settings?.dateFormat || '')
        setTimezone(data.settings?.timezone || '')
        setTheme(data.settings?.theme)
        if (data.twoFaBackupCodes) {
          setBackupCodes(data.twoFaBackupCodes)
        }
      } catch (error) {
        console.error('Error fetching user data:', error)
      }
    }

    fetchUserData()
  }, [])

  const handleSaveSettings = async () => {
    const newErrors = {}
    if (!dateFormat) newErrors.dateFormat = 'Date format is required'
    if (!timezone) newErrors.timezone = 'Timezone is required'

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    try {
      const { status } = await fetchWithAuth('users/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dateFormat, timezone, theme }),
      })

      setSnackbar({
        open: true,
        message:
          status === 200
            ? 'Settings saved successfully'
            : 'Error saving settings',
        severity: status === 200 ? 'success' : 'error',
      })
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Error saving settings',
        severity: 'error',
      })
    }
  }

  return (
    <Grid container spacing={3} size={12}>
      <Grid size={12}>
        <Card>
          <CardContent>
            <Stack direction="row" spacing={3} alignItems="center">
              <Avatar sx={{ width: 80, height: 80, bgcolor: 'primary.main' }}>
                <PersonIcon
                  sx={{ fontSize: 40, color: 'primary.contrastText' }}
                />
              </Avatar>
              <Box flex={1}>
                <Typography variant="h5" component="h1" gutterBottom>
                  {capitalize(user.profile?.firstName)}{' '}
                  {capitalize(user.profile?.lastName)}
                </Typography>
                <Typography variant="body1" color="text.secondary" gutterBottom>
                  {user.email}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <Card sx={{ height: '100%' }}>
          <CardContent>
            <Typography
              variant="h6"
              component="h2"
              gutterBottom
              color="primary"
            >
              User Details
            </Typography>
            <InfoItem
              label="Registration Date"
              value={
                user.createdAt && new Date(user.createdAt).toLocaleDateString()
              }
            />
            <InfoItem label="Role" value={user.role} />
          </CardContent>
        </Card>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <BackupCodesCard
          backupCodes={backupCodes}
          setBackupCodes={setBackupCodes}
          twoFaEnabled={user.twoFaEnabled}
          backupCodesCount={user.backupCodesCount}
        />
      </Grid>

      <Grid size={12}>
        <UserInformationCard
          dateFormat={dateFormat}
          setDateFormat={setDateFormat}
          timezone={timezone}
          setTimezone={setTimezone}
          setTheme={setTheme}
          onSaveSettings={handleSaveSettings}
          errors={errors}
        />
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Grid>
  )
}

export default UserProfilePage
